import * as t from 'io-ts';
import {isNumber, mapValues} from 'lodash';

import {
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedMetricTypesEnum,
} from '../../analytics/transformers/analyticsMetricsTransformersTypes';
import {makeEnumRuntimeType} from './apiTypes';

/**
 * Analytics models and parsers for metrics.
 */

/*
 * Runtime type.
 */

export enum AnalyticsMetricTypesEnum {
  HEAT_MAP = 'heat_map',
  SCALAR = 'scalar',
  TABLE = 'table',
  TIME_GRAPH = 'time_graph',
}

export enum AnalyticsDataPointTypesEnum {
  BUCKET = 'bucket',
  ACCOUNT = 'account',
  CARD = 'card',
  CONTACT = 'contact',
  DURATION = 'dur',
  INBOX = 'inbox',
  NUMERICAL = 'num',
  MESSAGE_TEMPLATE = 'message_template',
  PERCENTAGE = 'pct',
  PROGRESS = 'progress',
  RESOURCE = 'resource',
  RULE = 'rule',
  TAG = 'tag',
  TEAM = 'team',
  TEAMMATE = 'teammate',
  TEXT = 'str',
  SUBMETRIC = 'submetric',
}

export const analyticsCanBeHiddenColumnTypes = [
  AnalyticsDataPointTypesEnum.TEXT,
  AnalyticsDataPointTypesEnum.NUMERICAL,
  AnalyticsDataPointTypesEnum.PERCENTAGE,
  AnalyticsDataPointTypesEnum.PROGRESS,
  AnalyticsDataPointTypesEnum.DURATION,
];

const analyticsResourceDataPointTypes = [
  AnalyticsDataPointTypesEnum.ACCOUNT,
  AnalyticsDataPointTypesEnum.CARD,
  AnalyticsDataPointTypesEnum.CONTACT,
  AnalyticsDataPointTypesEnum.MESSAGE_TEMPLATE,
  AnalyticsDataPointTypesEnum.INBOX,
  AnalyticsDataPointTypesEnum.RULE,
  AnalyticsDataPointTypesEnum.TAG,
  AnalyticsDataPointTypesEnum.TEAMMATE,
  AnalyticsDataPointTypesEnum.TEAM,
];

const RAnalyticsNumericDataPointTypeDirty = t.union([
  t.literal(AnalyticsDataPointTypesEnum.DURATION),
  t.literal(AnalyticsDataPointTypesEnum.NUMERICAL),
  t.literal(AnalyticsDataPointTypesEnum.PERCENTAGE),
  t.literal(AnalyticsDataPointTypesEnum.PROGRESS),
]);

const RAnalyticsNumericDataPointDirty = t.type({
  type: RAnalyticsNumericDataPointTypeDirty,
  value: t.number,
  previousValue: t.number,
});

const RAnalyticsStringDataPointDirty = t.type({
  type: t.literal(AnalyticsDataPointTypesEnum.TEXT),
  value: t.string,
  previousValue: t.string,
});

const RAnalyticsSubmetricDataPointDirty = t.type({
  type: t.literal(AnalyticsDataPointTypesEnum.SUBMETRIC),
  value: t.string,
});

// Accounts can be undefined (not all contacts have accounts in the contacts table)
const RAnalyticsAccountDataPointDirty = t.intersection([
  t.type({
    type: t.literal(AnalyticsDataPointTypesEnum.ACCOUNT),
  }),
  t.partial({
    id: t.number,
    value: t.string,
    avatarUrl: t.string,
  }),
]);

const RAnalyticsMessageTemplateDataPointDirty = makeResourceDataPoint(
  AnalyticsDataPointTypesEnum.MESSAGE_TEMPLATE,
);

const RAnalyticsInboxDataPointDirty = makeResourceWithUrlDataPoint(AnalyticsDataPointTypesEnum.INBOX);

const RAnalyticsTagDataPointDirty = makeTagResourceDataPoint();

const RAnalyticsTeammateDataPointDirty = makeResourceWithUrlDataPoint(AnalyticsDataPointTypesEnum.TEAMMATE);

const RAnalyticsCardDataPointDirty = makeResourceWithOptionalAvatarUrlDataPoint(
  AnalyticsDataPointTypesEnum.CARD,
);

const RAnalyticsContactDataPointDirty = makeResourceWithOptionalAvatarUrlDataPoint(
  AnalyticsDataPointTypesEnum.CONTACT,
);

const RAnalyticsRuleDataPointDirty = makeResourceDataPoint(AnalyticsDataPointTypesEnum.RULE);

// Teams can be undefined (for instance viewing individual inboxes)
const RAnalyticsTeamDataPointDirty = t.intersection([
  t.type({
    type: t.literal(AnalyticsDataPointTypesEnum.TEAM),
  }),
  t.partial({
    id: t.number,
    value: t.string,
    url: t.string,
  }),
]);

// @TODO: Add other resources.
const RAnalyticsResourceDataPointDirty = t.union([
  RAnalyticsAccountDataPointDirty,
  RAnalyticsInboxDataPointDirty,
  RAnalyticsMessageTemplateDataPointDirty,
  RAnalyticsTagDataPointDirty,
  RAnalyticsTeammateDataPointDirty,
  RAnalyticsContactDataPointDirty,
  RAnalyticsCardDataPointDirty,
  RAnalyticsRuleDataPointDirty,
  RAnalyticsTeamDataPointDirty,
]);

const RAnalyticsKeyDataPointDirty = t.type({
  key: t.number,
  value: t.undefined,
});

const RAnalyticsBucketDataPointDirty = t.intersection([
  RAnalyticsKeyDataPointDirty,
  t.type({
    type: t.literal(AnalyticsDataPointTypesEnum.BUCKET),
  }),
]);

const RAnalyticsDataPointDirty = t.union([
  RAnalyticsNumericDataPointDirty,
  RAnalyticsStringDataPointDirty,
  RAnalyticsResourceDataPointDirty,
  RAnalyticsSubmetricDataPointDirty,
  RAnalyticsBucketDataPointDirty,
]);

function makeMetricType<
  ID extends
    | t.Type<AnalyticsScalarMetricsEnum, string | number>
    | t.Type<AnalyticsTableMetricsEnum, string | number>
    | t.Type<AnalyticsHeatMapMetricsEnum, string | number>
    | t.Type<AnalyticsGraphMetricsEnum, string | number>
    | t.Type<AnalyticsMultiGraphMetricsEnum, string | number>
    | t.Type<AnalyticsComputedGraphMetricsEnum, string | number>,
  Name extends AnalyticsMetricTypesEnum | AnalyticsComputedMetricTypesEnum,
  MetricType extends t.Mixed,
>(id: ID, name: Name, metricType: MetricType) {
  return t.intersection([
    t.type({
      id,
      metricType: t.literal(name),
      isEmpty: t.boolean,
    }),
    metricType,
  ]);
}

// SCALAR.

export enum AnalyticsScalarMetricsEnum {
  ACTIVE_SEGMENTS = 'num_active_segments_full',
  ACTIVE_TEAMMATES = 'num_active_teammates',
  ARCHIVED_SEGMENTS = 'num_archived_segments',
  ARCHIVED_SEGMENTS_WITH_REPLY = 'num_archived_segments_with_reply',
  AVG_FIRST_REPLY_TIME = 'avg_first_response_time',
  AVG_HANDLE_TIME = 'avg_handle_time',
  AVG_REACTION_TIME = 'avg_reaction_time',
  AVG_RESPONSE_TIME = 'avg_response_time',
  AVG_SLA_BREACH_TIME = 'avg_sla_breach_time',
  AVG_TOTAL_REPLY_TIME = 'avg_total_reply_time',
  MESSAGES_RECEIVED = 'num_messages_received',
  MESSAGES_SENT = 'num_messages_sent',
  NEW_SEGMENTS_COUNT = 'new_segments_count',
  NUM_SLA_TRIGGER = 'num_sla_trigger',
  NUM_CSAT_SURVEY_RESPONSE = 'num_csat_survey_response',
  // Used in the inbox heat map as a column name. FIXME: use the name naming `new_segments_count`.
  NUM_NEW_SEGMENTS = 'num_new_segments',
  PCT_CSAT_SURVEY_SATISFACTION = 'pct_csat_survey_satisfaction',
  PCT_MESSAGES_WITH_MESSAGE_TEMPLATES = 'pct_messages_with_message_templates',
  PCT_TAGGED_CONVERSATIONS = 'pct_tagged_conversations',
  PCT_SLA_ACHIEVED = 'pct_sla_achieved',
  SLA_BREACHES = 'num_sla_breach',
  SLA_WARNINGS = 'num_sla_warning',
}

export const RAnalyticsScalarMetricsEnum = makeEnumRuntimeType(AnalyticsScalarMetricsEnum);
const RAnalyticsScalarMetricDirty = makeMetricType(
  RAnalyticsScalarMetricsEnum,
  AnalyticsMetricTypesEnum.SCALAR,
  RAnalyticsNumericDataPointDirty,
);

const RAnalyticsScalarMetricsDictionaryDirty = t.record(
  RAnalyticsScalarMetricsEnum,
  RAnalyticsScalarMetricDirty,
);

// TABLE.

enum AnalyticsTableMetricSortsEnum {
  ASC = '+',
  DESC = '-',
}

export enum AnalyticsTableMetricsEnum {
  ACCOUNTS_TABLE = 'accounts_table',
  CUSTOMERS_TABLE_V2 = 'customers_table_v2',
  CSAT_SURVEY_RESPONSE_HISTO = 'csat_survey_response_histo',
  CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE = 'csat_survey_sample_feedback_table',
  INBOX_TABLE = 'conversations_inbox_table',
  MESSAGE_TEMPLATE_TABLE = 'message_template_table',
  RESOLUTION_TIME_HISTOGRAM = 'resolution_histo_v2',
  REPLY_TIME_HISTOGRAM = 'response_histo',
  FIRST_REPLY_TIME_HISTOGRAM = 'first_response_histo_v2',
  SANKEY_TABLE = 'sankey_table',
  SLA_STATUS_TABLE_V2 = 'sla_status_table_v2',
  TEAM_CHANGES = 'team_changes_table',
  TAGS_ALL_TABLE = 'tags_all_table',
  TAG_CHANGES_TABLE = 'tag_changes_table',
  TEAM_PERFORMANCE = 'team_performance_table_v2',
  TOP_TAGS = 'top_tags_table',
  WHAT_HAPPENED_TABLE = 'what_happened_table',
}

export const RAnalyticsTableMetricsEnum = makeEnumRuntimeType(AnalyticsTableMetricsEnum);

const RAnalyticsTableMetricColumnTypeDirty = t.union([
  t.literal(AnalyticsDataPointTypesEnum.ACCOUNT),
  t.literal(AnalyticsDataPointTypesEnum.BUCKET),
  t.literal(AnalyticsDataPointTypesEnum.DURATION),
  t.literal(AnalyticsDataPointTypesEnum.INBOX),
  t.literal(AnalyticsDataPointTypesEnum.NUMERICAL),
  t.literal(AnalyticsDataPointTypesEnum.MESSAGE_TEMPLATE),
  t.literal(AnalyticsDataPointTypesEnum.PERCENTAGE),
  t.literal(AnalyticsDataPointTypesEnum.PROGRESS),
  t.literal(AnalyticsDataPointTypesEnum.RULE),
  t.literal(AnalyticsDataPointTypesEnum.TAG),
  t.literal(AnalyticsDataPointTypesEnum.CONTACT),
  t.literal(AnalyticsDataPointTypesEnum.CARD),
  t.literal(AnalyticsDataPointTypesEnum.TEAMMATE),
  t.literal(AnalyticsDataPointTypesEnum.TEXT),
  t.literal(AnalyticsDataPointTypesEnum.RESOURCE),
  t.literal(AnalyticsDataPointTypesEnum.TEAM),
]);

const RAnalyticsTableMetricColumnDirty = t.type({
  id: t.string,
  from: t.union([t.undefined, t.string]),
  label: t.string,
  type: RAnalyticsTableMetricColumnTypeDirty,
  sort: t.union([
    t.undefined,
    t.literal(AnalyticsTableMetricSortsEnum.ASC),
    t.literal(AnalyticsTableMetricSortsEnum.DESC),
  ]),
  tooltip: t.union([t.undefined, t.string]),
});

const RAnalyticsTableMetricRowDirty = t.readonlyArray(RAnalyticsDataPointDirty);

const RAnalyticsTableMetricDirty = makeMetricType(
  RAnalyticsTableMetricsEnum,
  AnalyticsMetricTypesEnum.TABLE,
  t.type({
    columns: t.readonlyArray(RAnalyticsTableMetricColumnDirty),
    rows: t.readonlyArray(RAnalyticsTableMetricRowDirty),
  }),
);

const RAnalyticsTableMetricsDictionaryDirty = t.record(
  RAnalyticsTableMetricsEnum,
  RAnalyticsTableMetricDirty,
);

// HEATMAP.

export enum AnalyticsHeatMapMetricsEnum {
  INBOX = 'inbox_heat_map',
}

const RAnalyticsHeatMapMetricValueScalarDirty = t.record(
  makeEnumRuntimeType(AnalyticsScalarMetricsEnum),
  RAnalyticsNumericDataPointDirty,
);

const RAnalyticsHeatMapMetricValueDirty = t.type({
  day: t.number,
  hour: t.number,
  key: t.string,
  values: RAnalyticsHeatMapMetricValueScalarDirty,
});

export const RAnalyticsHeatMapMetricsEnum = makeEnumRuntimeType(AnalyticsHeatMapMetricsEnum);
const RAnalyticsHeatMapMetricDirty = makeMetricType(
  RAnalyticsHeatMapMetricsEnum,
  AnalyticsMetricTypesEnum.HEAT_MAP,
  t.type({
    subMetrics: t.readonlyArray(RAnalyticsTableMetricColumnDirty),
    values: t.readonlyArray(RAnalyticsHeatMapMetricValueDirty),
  }),
);

const RAnalyticsHeatMapMetricsDictionaryDirty = t.record(
  RAnalyticsHeatMapMetricsEnum,
  RAnalyticsHeatMapMetricDirty,
);

// DONUT.

const RAnalyticsDonutMetricValueDirty = t.intersection([
  t.type({
    label: t.string,
  }),
  RAnalyticsNumericDataPointDirty,
]);

const RAnalyticsDonutMetricDirty = makeMetricType(
  makeEnumRuntimeType(AnalyticsComputedGraphMetricsEnum),
  AnalyticsComputedMetricTypesEnum.CUSTOM_GRAPH,
  t.type({
    values: t.readonlyArray(RAnalyticsDonutMetricValueDirty),
  }),
);

// GRAPH.

export enum AnalyticsGraphMetricsEnum {
  ACTIVE_SEGMENTS_GRAPH = 'num_active_segments_full_graph',
  ARCHIVED_SEGMENTS_GRAPH = 'archived_segments_graph',
  ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH = 'archived_segments_with_reply_graph',
  CSAT_SURVEY_RESPONSE_GRAPH = 'csat_survey_response_graph',
  CSAT_SURVEY_SATISFACTION_GRAPH = 'csat_survey_satisfaction_graph',
  FIRST_RESPONSE_GRAPH = 'first_response_graph',
  HANDLE_TIME_GRAPH = 'handle_time_graph',
  MESSAGES_RECEIVED_GRAPH = 'messages_received_graph',
  NEW_SEGMENTS_GRAPH = 'new_segments_graph',
  NUM_MESSAGES_SENT_GRAPH = 'num_messages_sent_graph',
  NUM_SLA_BREACH_GRAPH = 'num_sla_breach_graph',
  NUM_SLA_WARNING_GRAPH = 'num_sla_warning_graph',
  PCT_SLA_ACHIEVED_GRAPH = 'pct_sla_achieved_graph',
  RESPONSE_GRAPH = 'response_graph',
}

export const RAnalyticsGraphMetricValue = t.intersection([
  t.type({
    label: t.string,
    previousStart: t.number,
    previousEnd: t.number,
    start: t.number,
    end: t.number,
  }),
  RAnalyticsNumericDataPointDirty,
]);

export const RAnalyticsGraphMetricsEnum = makeEnumRuntimeType(AnalyticsGraphMetricsEnum);
const RAnalyticsGraphMetricDirty = makeMetricType(
  RAnalyticsGraphMetricsEnum,
  AnalyticsMetricTypesEnum.TIME_GRAPH,
  t.type({
    values: t.readonlyArray(RAnalyticsGraphMetricValue),
  }),
);

const RAnalyticsGraphMetricsDictionaryDirty = t.record(
  RAnalyticsGraphMetricsEnum,
  RAnalyticsGraphMetricDirty,
);

// MULTI GRAPH.

export enum AnalyticsMultiGraphMetricsEnum {
  SLA_PERFORMANCE_GRAPH = 'sla_performance_graph',
}
const RAnalyticsMultiGraphMetricsEnum = makeEnumRuntimeType(AnalyticsMultiGraphMetricsEnum);

const RAnalyticsMultiGraphMetricValueDirty = t.type({
  label: t.string,
  start: t.number,
  end: t.number,
  values: t.record(t.string, RAnalyticsNumericDataPointDirty),
});

const RAnalyticsMultiGraphMetricDirty = makeMetricType(
  RAnalyticsMultiGraphMetricsEnum,
  AnalyticsMetricTypesEnum.TIME_GRAPH,
  t.type({
    values: t.readonlyArray(RAnalyticsMultiGraphMetricValueDirty),
  }),
);

// METRIC.

export const RAnalyticsMetricDirty = t.union([
  RAnalyticsHeatMapMetricDirty,
  RAnalyticsScalarMetricDirty,
  RAnalyticsTableMetricDirty,
  RAnalyticsGraphMetricDirty,
  RAnalyticsMultiGraphMetricDirty,
  RAnalyticsDonutMetricDirty,
]);

export const RAnalyticsMetricsDictionaryDirty = t.union([
  RAnalyticsHeatMapMetricsDictionaryDirty,
  RAnalyticsScalarMetricsDictionaryDirty,
  RAnalyticsTableMetricsDictionaryDirty,
  RAnalyticsGraphMetricsDictionaryDirty,
]);

export const RAnalyticsMetricsKeysDirty = t.union([
  RAnalyticsScalarMetricsEnum,
  RAnalyticsTableMetricsEnum,
  RAnalyticsGraphMetricsEnum,
  RAnalyticsMultiGraphMetricsEnum,
  RAnalyticsHeatMapMetricsEnum,
]);

// ERROR METRIC.

export enum AnalyticsMetricErrorTypesEnum {
  TIMED_OUT = 'timed_out',
}
const RAnalyticsMetricErrorTypesEnum = makeEnumRuntimeType(AnalyticsMetricErrorTypesEnum);

const RAnalyticsMetricErrorDirty = t.type({
  id: RAnalyticsMetricsKeysDirty,
  errorType: RAnalyticsMetricErrorTypesEnum,
});

/*
 * Type.
 */

export type AnalyticsNumericDataPointType = t.TypeOf<typeof RAnalyticsNumericDataPointTypeDirty>;
export interface AnalyticsNumericDataPoint extends t.TypeOf<typeof RAnalyticsNumericDataPointDirty> {}
export interface AnalyticsStringDataPoint extends t.TypeOf<typeof RAnalyticsStringDataPointDirty> {}
export interface AnalyticsBucketDataPoint extends t.TypeOf<typeof RAnalyticsBucketDataPointDirty> {}
export interface AnalyticsSubmetricDataPoint extends t.TypeOf<typeof RAnalyticsSubmetricDataPointDirty> {}
export type AnalyticsResourceDataPoint = t.TypeOf<typeof RAnalyticsResourceDataPointDirty>;
export type AnalyticsDataPoint = t.TypeOf<typeof RAnalyticsDataPointDirty>;
export type AnalyticsTagDataPoint = t.TypeOf<typeof RAnalyticsTagDataPointDirty>;
export type AnalyticsAccountDataPoint = t.TypeOf<typeof RAnalyticsAccountDataPointDirty>;
export type AnalyticsContactDataPoint = t.TypeOf<typeof RAnalyticsContactDataPointDirty>;
export type AnalyticsCardDataPoint = t.TypeOf<typeof RAnalyticsCardDataPointDirty>;
export type AnalyticsTeamDataPoint = t.TypeOf<typeof RAnalyticsTeamDataPointDirty>;
export type AnalyticsTeammateDataPoint = t.TypeOf<typeof RAnalyticsTeammateDataPointDirty>;
export type AnalyticsInboxDataPoint = t.TypeOf<typeof RAnalyticsInboxDataPointDirty>;
export type AnalyticsMessageTemplateDataPoint = t.TypeOf<typeof RAnalyticsMessageTemplateDataPointDirty>;

export interface AnalyticsGraphMetricValue extends t.TypeOf<typeof RAnalyticsGraphMetricValue> {}
export interface AnalyticsGraphMetric extends t.TypeOf<typeof RAnalyticsGraphMetricDirty> {}

export interface AnalyticsDonutMetricValue extends t.TypeOf<typeof RAnalyticsDonutMetricValueDirty> {}
export interface AnalyticsDonutMetric extends t.TypeOf<typeof RAnalyticsDonutMetricDirty> {}

export interface AnalyticsMultiGraphMetric extends t.TypeOf<typeof RAnalyticsMultiGraphMetricDirty> {}

export interface AnalyticsScalarMetric extends t.TypeOf<typeof RAnalyticsScalarMetricDirty> {}

export type AnalyticsTableMetricColumnType = t.TypeOf<typeof RAnalyticsTableMetricColumnTypeDirty>;
export interface AnalyticsTableMetricColumn extends t.TypeOf<typeof RAnalyticsTableMetricColumnDirty> {}
export interface AnalyticsTableMetricRow extends t.TypeOf<typeof RAnalyticsTableMetricRowDirty> {}
export interface AnalyticsTableMetric extends t.TypeOf<typeof RAnalyticsTableMetricDirty> {}

interface AnalyticsHeatMapMetricValueScalar
  extends t.TypeOf<typeof RAnalyticsHeatMapMetricValueScalarDirty> {}
export interface AnalyticsHeatMapMetricValue extends t.TypeOf<typeof RAnalyticsHeatMapMetricValueDirty> {}
export interface AnalyticsHeatMapMetric extends t.TypeOf<typeof RAnalyticsHeatMapMetricDirty> {}

export type AnalyticsMetric = t.TypeOf<typeof RAnalyticsMetricDirty>;
export type AnalyticsMetricsDictionary = t.TypeOf<typeof RAnalyticsMetricsDictionaryDirty>;
export type AnalyticsMetricsKeys = t.TypeOf<typeof RAnalyticsMetricsKeysDirty>;
export type AnalyticsMetricError = t.TypeOf<typeof RAnalyticsMetricErrorDirty>;

export const RAnalyticsMetricsKeys = t.clean(RAnalyticsMetricsKeysDirty);

/*
 * IO methods.
 */

function parseAnalyticsNumericDataPoint(src: any): AnalyticsNumericDataPoint {
  return {
    type: src.t,
    value: src.v,
    previousValue: src.p,
  };
}

function parseAnalyticsStringDataPoint(src: any): AnalyticsStringDataPoint {
  return {
    type: src.t,
    value: src.v,
    previousValue: src.p,
  };
}

function parseAnalyticsBucketDataPoint(src: any): AnalyticsBucketDataPoint {
  return {
    type: src.t,
    key: src.key,
    value: undefined,
  };
}

function parseAnalyticsResourceDataPoint(src: any): AnalyticsResourceDataPoint {
  if (analyticsResourceDataPointTypes.includes(src.t)) {
    const dataPoint = {
      type: src.t,
      value: src.v,
      url: src.url,
      id: src.id,
      avatarUrl: src.avatar_url,
    };

    if (src.t === AnalyticsDataPointTypesEnum.TAG) {
      return {
        ...dataPoint,
        isArchived: src.is_archived || false,
      };
    }

    return dataPoint;
  }

  return src;
}

function parseAnalyticsDataPoint(
  src: any,
):
  | AnalyticsNumericDataPoint
  | AnalyticsStringDataPoint
  | AnalyticsResourceDataPoint
  | AnalyticsBucketDataPoint {
  if (
    [
      AnalyticsDataPointTypesEnum.DURATION,
      AnalyticsDataPointTypesEnum.NUMERICAL,
      AnalyticsDataPointTypesEnum.PERCENTAGE,
      AnalyticsDataPointTypesEnum.PROGRESS,
    ].includes(src.t)
  ) {
    return parseAnalyticsNumericDataPoint(src);
  }

  if ([AnalyticsDataPointTypesEnum.TEXT, AnalyticsDataPointTypesEnum.SUBMETRIC].includes(src.t)) {
    return parseAnalyticsStringDataPoint(src);
  }

  if (src.t === AnalyticsDataPointTypesEnum.BUCKET) {
    return parseAnalyticsBucketDataPoint(src);
  }

  return parseAnalyticsResourceDataPoint(src);
}

function parseAnalyticsMultiGraphMetric(src: any): AnalyticsMultiGraphMetric {
  return {
    id: src.id,
    metricType: AnalyticsMetricTypesEnum.TIME_GRAPH,
    isEmpty: src.is_empty ?? false,
    values: src.vals.map((val: any) => ({
      label: val.label,
      start: val.start,
      end: val.end,
      values: mapValues(val.graph_values, parseAnalyticsNumericDataPoint),
    })),
  };
}

function parseAnalyticsHeatMapMetricValue(src: any): AnalyticsHeatMapMetricValue {
  // Heat map keys are 'day/hour' where  day {1,7}, hour {0,23}.
  const [dayStr, hourStr] = src.key.split('/');

  return {
    day: Number.parseInt(dayStr, 10),
    hour: Number.parseInt(hourStr, 10),
    key: src.key,
    values: Object.entries(src.values || {}).reduce((acc, [metricKey, value]) => {
      const parsedValue = parseAnalyticsDataPoint(value);

      return {
        ...acc,
        [metricKey]: parsedValue,
      };
    }, {} as AnalyticsHeatMapMetricValueScalar),
  };
}

export function parseAnalyticsScalarMetric(src: any): AnalyticsScalarMetric {
  return {
    id: src.id,
    metricType: AnalyticsMetricTypesEnum.SCALAR,
    isEmpty: src.is_empty ?? false,
    ...parseAnalyticsNumericDataPoint(src),
  };
}

export function parseAnalyticsTableMetric(src: any): AnalyticsTableMetric {
  return {
    id: src.id,
    metricType: AnalyticsMetricTypesEnum.TABLE,
    columns: src.columns,
    isEmpty: src.is_empty ?? false,
    rows: src.rows.map((row: any) => row.map(parseAnalyticsDataPoint)),
  };
}

export function parseAnalyticsGraphMetric(src: any): AnalyticsGraphMetric | AnalyticsMultiGraphMetric {
  // If the id is belongs to a multi-graph, like `sla_performance_graph` parse it as one.
  if (RAnalyticsMultiGraphMetricsEnum.is(src.id)) {
    return parseAnalyticsMultiGraphMetric(src);
  }

  const startPeriod = src.vals[0]?.start ?? 0;
  const endPeriod = src.vals[src.vals.length - 1]?.end ?? 0;
  const startPrevious = startPeriod - (endPeriod - startPeriod);
  const offset = startPeriod - startPrevious;

  return {
    id: src.id,
    metricType: AnalyticsMetricTypesEnum.TIME_GRAPH,
    isEmpty: src.is_empty ?? false,
    values: src.vals.map((val: any) => ({
      label: val.label,
      start: val.start,
      end: val.end,

      previousStart: val.start - offset,
      previousEnd: val.end - offset,

      ...parseAnalyticsNumericDataPoint(val),
    })),
  };
}

export function parseAnalyticsHeatMapMetric(src: any): AnalyticsHeatMapMetric {
  return {
    id: src.id,
    metricType: AnalyticsMetricTypesEnum.HEAT_MAP,
    isEmpty: src.is_empty ?? false,
    subMetrics: src.sub_metrics,
    values: src.values.map(parseAnalyticsHeatMapMetricValue),
  };
}

/*
 * Helpers.
 */

function makeResourceDataPoint<T extends AnalyticsDataPointTypesEnum>(type: T) {
  return t.type({
    type: t.literal(type),
    id: t.number,
    value: t.string,
  });
}

function makeResourceWithUrlDataPoint<T extends AnalyticsDataPointTypesEnum>(type: T) {
  return t.intersection([makeResourceDataPoint(type), t.type({url: t.string})]);
}

function makeResourceWithOptionalAvatarUrlDataPoint<T extends AnalyticsDataPointTypesEnum>(type: T) {
  return t.intersection([makeResourceDataPoint(type), t.partial({avatarUrl: t.string})]);
}

function makeTagResourceDataPoint() {
  return t.intersection([
    makeResourceWithUrlDataPoint(AnalyticsDataPointTypesEnum.TAG),
    t.type({isArchived: t.boolean}),
  ]);
}

/*
 * Type guards.
 */

export function isAnalyticsMetricKey(metricKey: any): metricKey is AnalyticsMetricsKeys {
  return RAnalyticsMetricsKeys.is(metricKey);
}

export function isNumericDataPointType(
  datapointType: AnalyticsDataPointTypesEnum,
): datapointType is AnalyticsNumericDataPointType {
  return RAnalyticsNumericDataPointTypeDirty.is(datapointType);
}

export function isNumericDataPoint(datapoint: any): datapoint is AnalyticsNumericDataPoint {
  return (
    RAnalyticsNumericDataPointTypeDirty.is(datapoint.type) &&
    isNumber(datapoint.value) &&
    isNumber(datapoint.previousValue)
  );
}

export function isStringDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsStringDataPoint {
  return RAnalyticsStringDataPointDirty.is(dataPoint);
}

export function isSubmetricDataPoint(
  dataPoint: AnalyticsDataPoint,
): dataPoint is AnalyticsSubmetricDataPoint {
  return RAnalyticsSubmetricDataPointDirty.is(dataPoint);
}

export function isAccountDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsAccountDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.ACCOUNT;
}

export function isContactDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsContactDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.CONTACT;
}

export function isCardDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsCardDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.CARD;
}

export function isTagDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsTagDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.TAG;
}

export function isTeammateDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsTeammateDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.TEAMMATE;
}

export function isMessageTemplateDataPoint(
  dataPoint: AnalyticsDataPoint,
): dataPoint is AnalyticsMessageTemplateDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.MESSAGE_TEMPLATE;
}

export function isInboxDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsInboxDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.INBOX;
}

export function isTeamDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsTeamDataPoint {
  return dataPoint.type === AnalyticsDataPointTypesEnum.TEAM;
}

export function isResourceDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsResourceDataPoint {
  return analyticsResourceDataPointTypes.includes(dataPoint.type);
}

export function isBucketDataPoint(dataPoint: AnalyticsDataPoint): dataPoint is AnalyticsBucketDataPoint {
  return RAnalyticsBucketDataPointDirty.is(dataPoint);
}
